import { useTranscriber } from "../hooks/useTranscriber";
import { AudioManager } from "../components/AudioManager";
import Transcript from "../components/Transcript";
import { supabase } from "../utils/supabase";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../contexts/ToastContext";
import { motion } from "framer-motion";
import { Skeleton } from "../components/SkeletonLoader";

export default function Home() {
    const transcriber = useTranscriber();
    const { user } = useAuth();
    const { showToast } = useToast();

    const saveTranscription = async (
        text: string,
        audioUrl: string,
        duration: number,
        language: string,
        model: string
    ) => {
        if (!user) return;

        try {
            const { error } = await supabase.from("transcriptions").insert({
                user_id: user.id,
                text,
                audio_url: audioUrl,
                duration,
                language,
                model,
            });

            if (error) throw error;
            showToast("Transcription saved successfully", "success");
        } catch (error) {
            console.error("Error saving transcription:", error);
            showToast("Failed to save transcription", "error");
        }
    };

    // Simplified animation for page elements
    const fadeIn = {
        initial: { opacity: 0, y: 10 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.5, ease: "easeOut" }
    };

    return (
        <div className="flex flex-col items-center justify-center max-w-xl mx-auto space-y-8">
            <motion.div
                initial={fadeIn.initial}
                animate={fadeIn.animate}
                transition={fadeIn.transition}
                className="text-center"
            >
                <div className="flex justify-center mb-4">
                    <div className="relative">
                        <svg className="h-16 w-16 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                        </svg>
                        <div className="absolute -right-1 -top-1 rounded-full bg-success flex items-center justify-center w-6 h-6 text-white">
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                            </svg>
                        </div>
                    </div>
                </div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white leading-tight mb-3">
                    Transform Your Voice Into Text
                </h1>
                <p className="text-text-secondary max-w-md mx-auto mb-2">
                    Powered by advanced machine learning, right in your browser.
                </p>
            </motion.div>

            <motion.div
                initial={fadeIn.initial}
                animate={fadeIn.animate}
                transition={{ ...fadeIn.transition, delay: 0.2 }}
                className="w-full"
            >
                <div className="card p-6 sm:p-5 border border-primary/20 bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm">
                    {/* Audio Manager */}
                    <AudioManager
                        transcriber={transcriber}
                        onTranscriptionComplete={saveTranscription}
                    />

                    {/* Show skeleton loader or transcript */}
                    <div className="mt-8">
                        {transcriber.isModelLoading ? (
                            <div className="card animate-pulse p-5">
                                <div className="flex justify-center mb-3">
                                    <Skeleton width="60%" height="1.5rem" variant="text" />
                                </div>
                                <Skeleton height="200px" variant="rounded" className="mb-4" />
                            </div>
                        ) : (
                            <Transcript transcribedData={transcriber.output} />
                        )}
                    </div>
                </div>
            </motion.div>
        </div>
    );
}